import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { FaSearch, FaUser, FaFilePdf, FaEye, FaArrowLeft, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { Loader2, ChevronRight, FileText } from 'lucide-react';

const EngineerDocumentDetails = () => {
    const [engineers, setEngineers] = useState([]);
    const [selectedEngineer, setSelectedEngineer] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [loading, setLoading] = useState(true);
    const [fetchingDocs, setFetchingDocs] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const documents = [
        { id: 1, name: "शासनमान्य संस्थेचे प्रमाणपत्र", englishName: "Accredited Certificate" },
        { id: 2, name: " शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक.", englishName: "Statutory Institute Marksheet" },
        { id: 3, name: "अधिवस प्रमाणपत्र", englishName: "Domicile Certificate" },
        { id: 4, name: "नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो लावून फोटो स्वसाक्षांकित करावा", englishName: "Self Attested Photo Document" },
        { id: 5, name: "आय कार्ड साईझ फोटो", englishName: "ID Card Size Photo" },
        { id: 6, name: "अर्जदाराची स्वाक्षरी", englishName: "Applicant Signature" },
        { id: 7, name: "आधार कार्ड", englishName: "Aadhar Card" },
        { id: 8, name: "पॅन कार्ड", englishName: "PAN Card" },
        { id: 9, name: "प्रतिज्ञा पत्र", englishName: "Affidavit" },
        { id: 10, name: "रोजगार कार्ड", englishName: "Employment Card" },
        { id: 11, name: "DSC टोकन", englishName: "DSC Token" },
        { id: 12, name: "Other Document", englishName: "Other Document" },
    ];

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                navigate('/login');
            } else {
                // Fetch current user's role
                const userRef = ref(db, `ekamwatapzp/users/${user.uid}`);
                onValue(userRef, (snapshot) => {
                    if (snapshot.exists()) {
                        setUserRole(snapshot.val().role);
                    }
                });
            }
        });

        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(engRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key],
                    docCount: data[key].documents ? Object.keys(data[key].documents).length : 0
                }));
                setEngineers(list.reverse());
            } else {
                setEngineers([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleDocumentApproval = async (docId, isApproved) => {
        if (!selectedEngineer || !userRole) return;
        
        const roleField = `approvedBy${userRole.charAt(0).toUpperCase() + userRole.slice(1)}`;
        const docRef = ref(db, `ekamwatapzp/EngineerRegistrations/${selectedEngineer.id}/documents/${docId}`);
        
        try {
            await update(docRef, { [roleField]: isApproved });
            // Update local state to show immediate feedback
            setUploadedFiles(prev => ({
                ...prev,
                [docId]: {
                    ...prev[docId],
                    [roleField]: isApproved
                }
            }));
        } catch (error) {
            console.error("Error updating approval:", error);
        }
    };

    const fetchUserDocuments = (engineer) => {
        setFetchingDocs(true);
        setSelectedEngineer(engineer);
        const docsRef = ref(db, `ekamwatapzp/EngineerRegistrations/${engineer.id}/documents`);
        
        onValue(docsRef, (snapshot) => {
            if (snapshot.exists()) {
                setUploadedFiles(snapshot.val());
            } else {
                setUploadedFiles({});
            }
            setFetchingDocs(false);
        }, { onlyOnce: true });
    };

    const getDocStats = (engDocs) => {
        if (!userRole) return { approved: 0, rejected: 0, pending: 0, totalUploaded: 0 };
        const roleField = `approvedBy${userRole.charAt(0).toUpperCase() + userRole.slice(1)}`;
        let approved = 0;
        let rejected = 0;
        const docs = engDocs ? Object.values(engDocs) : [];
        docs.forEach(d => {
            if (d[roleField] === true) approved++;
            else if (d[roleField] === false) rejected++;
        });
        return { 
            approved, 
            rejected, 
            pending: docs.length - (approved + rejected),
            totalUploaded: docs.length 
        };
    };

    const filteredEngineers = engineers.filter(eng => {
        const fullName = `${eng.firstName} ${eng.surname}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || eng.whatsappNo?.includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-12 w-12 text-blue-600" />
            </div>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-8 bg-slate-50 min-h-screen">
                {!selectedEngineer ? (
                    <div className="animate-in fade-in duration-500">
                        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800 tracking-tight">डॉक्युमेंट डिटेल्स - अभियंता यादी</h1>
                                <p className="text-gray-500 text-sm mt-1">कागदपत्रे पाहण्यासाठी अभियंता निवडा</p>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4">
                            <div className="flex-grow relative">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="text" 
                                    placeholder="नाव किंवा मोबाईल नंबर शोध..." 
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-900 text-white">
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider border-b border-gray-800 text-center">नाव</th>
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider border-b border-gray-800 text-center">मोबाईल</th>
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider border-b border-gray-800 text-center">अपलोड</th>
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider border-b border-gray-800 text-center">तपशील</th>
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider border-b border-gray-800 text-center">स्थिती</th>
                                            <th className="px-6 py-4 text-xs font-black uppercase tracking-wider text-center border-b border-gray-800">कृती</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredEngineers.map((item) => (
                                            <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                                                            <FaUser />
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-gray-800">{item.firstName} {item.surname}</div>
                                                            <div className="text-[10px] text-gray-400 font-bold uppercase">{item.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-700">{item.whatsappNo}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-bold text-[11px] ring-1 ring-blue-100">
                                                        {item.docCount} / 12
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {(() => {
                                                        const stats = getDocStats(item.documents);
                                                        return (
                                                            <div className="flex flex-col gap-1 items-center">
                                                                <div className="flex gap-1">
                                                                    <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[9px] font-black border border-green-200">A: {stats.approved}</span>
                                                                    <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[9px] font-black border border-red-200">R: {stats.rejected}</span>
                                                                    <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-[9px] font-black border border-yellow-200">P: {stats.pending}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {(() => {
                                                        const stats = getDocStats(item.documents);
                                                        const isAllApproved = stats.approved === 12;
                                                        return (
                                                            <span className={`px-2 py-1 rounded text-[10px] font-black border uppercase shadow-sm ${
                                                                isAllApproved ? 'bg-green-600 text-white border-green-700' : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                                                            }`}>
                                                                {isAllApproved ? 'मंजूर' : 'प्रलंबित'}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button 
                                                        onClick={() => fetchUserDocuments(item)}
                                                        className="text-white bg-blue-600 hover:bg-blue-700 text-[10px] font-black px-4 py-2 rounded-lg transition-all shadow-md active:scale-95"
                                                    >
                                                        डॉक्युमेंट पहा
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {filteredEngineers.length === 0 && (
                                    <div className="p-12 text-center text-gray-400 italic font-medium">
                                        कोणतीही नोंदणी आढळली नाही.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="animate-in slide-in-from-right-8 duration-500">
                        {/* Header & Back Button */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                            <button 
                                onClick={() => setSelectedEngineer(null)}
                                className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors font-bold bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200"
                            >
                                <FaArrowLeft /> परता
                            </button>
                            <div className="text-right">
                                <h2 className="text-xl font-extrabold text-blue-900 leading-tight">
                                    {selectedEngineer.firstName} {selectedEngineer.surname}
                                </h2>
                                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">{selectedEngineer.whatsappNo}</p>
                            </div>
                        </div>

                        {fetchingDocs ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
                                <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Fetching User Documents...</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden mb-10">
                                <div className="p-1 md:p-6">
                                    <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                    <th className="px-4 py-5 font-bold text-slate-700 text-xs w-16 text-center border-r border-slate-100">#</th>
                                                    <th className="px-6 py-5 text-left border-r border-slate-100 font-bold text-slate-700 text-xs italic">Document Category</th>
                                                    <th className="px-6 py-5 text-center border-r border-slate-100 font-bold text-slate-700 text-xs">Status</th>
                                                    <th className="px-6 py-5 text-center border-r border-slate-100 font-bold text-slate-700 text-xs">Approval</th>
                                                    <th className="px-6 py-5 text-center font-bold text-slate-700 text-xs">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {documents.map((doc) => {
                                                    const uploaded = uploadedFiles[doc.id];
                                                    return (
                                                        <tr key={doc.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                                            <td className="px-4 py-6 border-r border-slate-50 text-center font-bold text-slate-400 text-sm">
                                                                {doc.id}
                                                            </td>
                                                            <td className="px-6 py-6 border-r border-slate-50">
                                                                <div className="flex flex-col">
                                                                    <span className="font-bold text-slate-800 text-[15px]">{doc.name}</span>
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{doc.englishName}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-6 border-r border-slate-50 text-center">
                                                                {uploaded ? (
                                                                    <div className="flex items-center justify-center gap-2 text-emerald-600 font-bold text-[10px] bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 mx-auto w-fit uppercase">
                                                                        <FaCheckCircle /> Uploaded
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-center gap-2 text-rose-400 font-bold text-[10px] bg-rose-50 px-3 py-1 rounded-full border border-rose-100 mx-auto w-fit uppercase">
                                                                        <FaTimesCircle /> Not Found
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-6 border-r border-slate-50 text-center">
                                                                <div className="flex flex-col items-center gap-2">
                                                                    <div className="flex gap-2">
                                                                        <button 
                                                                            onClick={() => handleDocumentApproval(doc.id, true)}
                                                                            disabled={!uploaded}
                                                                            className={`p-2 rounded-lg transition-all ${uploaded && uploaded[`approvedBy${userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}`] === true ? 'bg-green-600 text-white shadow-md' : 'bg-slate-100 text-slate-400 hover:bg-green-50 hover:text-green-600'}`}
                                                                            title="Accept"
                                                                        >
                                                                            <FaCheckCircle size={14} />
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleDocumentApproval(doc.id, false)}
                                                                            disabled={!uploaded}
                                                                            className={`p-2 rounded-lg transition-all ${uploaded && uploaded[`approvedBy${userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}`] === false ? 'bg-red-600 text-white shadow-md' : 'bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-600'}`}
                                                                            title="Reject"
                                                                        >
                                                                            <FaTimesCircle size={14} />
                                                                        </button>
                                                                    </div>
                                                                    {uploaded && uploaded[`approvedBy${userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}`] !== undefined && (
                                                                        <span className={`text-[8px] font-black uppercase tracking-tighter ${uploaded[`approvedBy${userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}`] ? 'text-green-600' : 'text-red-600'}`}>
                                                                            {uploaded[`approvedBy${userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}`] ? 'Accepted by you' : 'Rejected by you'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-6 font-bold text-center">
                                                                <div className="flex items-center justify-center">
                                                                    {uploaded ? (
                                                                        <a 
                                                                            href={uploaded.url} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl font-bold transition-all shadow-md text-[10px] bg-blue-600 text-white hover:bg-blue-700 active:scale-95 uppercase tracking-widest"
                                                                        >
                                                                            <FaEye /> View
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-slate-300 text-[10px] font-black italic tracking-tighter">PENDING</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </EngineerLayout>
    );
};

export default EngineerDocumentDetails;
